import { Button } from '@/components/ui/button'
import { formatVolume } from '@/lib/table-utils'
import { cn } from '@/lib/utils'
import type { EventListItem } from '@/types/market'
import dayjs from 'dayjs'
import Link from 'next/link'
import { getMarketUrl } from './utils'

type ExpandedMarketsTableProps = {
	event: EventListItem
	eventKey: string
}

export function ExpandedMarketsTable({ event, eventKey }: ExpandedMarketsTableProps) {
	const hasExpandableMarkets = event.marketCount >= 1 && event.markets && event.markets.length > 0

	if (!hasExpandableMarkets) {
		return null
	}

	return (
		<>
			{event.markets?.map(market => {
				const isMarketClosed = market.status === 'closed'
				return (
					<tr
						key={`${eventKey}-${market.id}`}
						className={cn('cursor-pointer bg-brand-dark-light', 'border-b border-b-[#282A2C]/50')}
					>
						<td className="p-[16px] pl-[74px]">
							<Link
								href={getMarketUrl({
									platform: event.platform,
									id: market.id,
									parentEvent: {
										parentEventSlug: event.id,
										parentEventId: null,
									},
								})}
							>
								<span
									className={cn(
										'font-grotesk font-normal text-[14px] leading-[100%]',
										isMarketClosed ? 'text-muted-foreground' : 'text-white'
									)}
								>
									{market.title}
								</span>
							</Link>
						</td>
						<td className="px-3 py-2 text-left">
							<span
								className={cn(
									'pl-[4px] font-grotesk font-normal text-[14px] leading-[100%]',
									isMarketClosed ? 'text-muted-foreground' : 'text-white'
								)}
							>
								{formatVolume(event.volume)}
							</span>
						</td>
						<td className="px-3 py-2 text-left">
							<span
								className={cn(
									'pl-[4px] font-grotesk font-normal text-[14px] leading-[100%]',
									isMarketClosed ? 'text-muted-foreground' : 'text-white'
								)}
							>
								{formatVolume(event.liquidity)}
							</span>
						</td>
						<td className="px-3 py-2">
							<span
								className={cn(
									'whitespace-nowrap pl-[4px] font-grotesk font-normal text-[14px] leading-[100%]',
									isMarketClosed ? 'text-muted-foreground' : 'text-white'
								)}
							>
								{isMarketClosed
									? 'Closed'
									: event.endDate
										? dayjs.unix((event.endDate * 1000) / 1000).format('MMM D, YYYY, h:mm A')
										: '--'}
							</span>
						</td>
						<td className="px-3 py-2">
							<div className="flex items-center gap-[6px]">
								<Button
									asChild
									className="h-[32px] w-[95px] rounded-[16px] bg-[#4ADE8026] font-grotesk font-medium text-[#4ADE80] hover:bg-[#4ADE8026]/70"
								>
									<Link
										href={getMarketUrl(
											{
												platform: event.platform,
												id: market.id,
												parentEvent: {
													parentEventSlug: event.id,
													parentEventId: null,
												},
											},
											'yes'
										)}
									>
										Yes{' '}
										{typeof market.yesPrice === 'number'
											? `${Math.round(market.yesPrice * 100)}¢`
											: ''}
									</Link>
								</Button>
								<Button
									asChild
									className="h-[32px] w-[95px] rounded-[16px] bg-[#EF444426] font-grotesk font-medium text-[#EF4444] hover:bg-[#EF444426]/70"
								>
									<Link
										href={getMarketUrl(
											{
												platform: event.platform,
												id: market.id,
												parentEvent: {
													parentEventSlug: event.id,
													parentEventId: null,
												},
											},
											'no'
										)}
									>
										No{' '}
										{typeof market.yesPrice === 'number'
											? `${Math.round((1 - market.yesPrice) * 100)}¢`
											: ''}
									</Link>
								</Button>
							</div>
						</td>
					</tr>
				)
			})}
		</>
	)
}
